import {
  ShowBase,
  useShowContext,
  useUpdate,
  useRefresh,
  useDelete,
  useNotify,
} from "ra-core";
import { Link, useNavigate } from "react-router";
import { useState } from "react";
import {
  ArrowLeft,
  Receipt,
  Briefcase,
  FileText,
  Calendar,
  DollarSign,
  CheckCircle,
  Activity,
  Pencil,
  Save,
  X,
  Trash2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ReferenceField } from "@/components/admin/reference-field";
import { useRecordContext } from "ra-core";
import type { Invoice } from "../../providers/fakerest/dataGenerator/types";

const statusColors: Record<string, string> = {
  pending:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  paid: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  overdue: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

const statusChoices = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];

export const InvoiceShow = () => {
  return (
    <ShowBase>
      <InvoiceShowContent />
    </ShowBase>
  );
};

const InvoiceShowContent = () => {
  const { record, isPending } = useShowContext<Invoice>();
  const [update, { isPending: isUpdating }] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const notify = useNotify();
  const [isEditing, setIsEditing] = useState(false);

  const handleDelete = () => {
    deleteOne(
      "invoices",
      { id: record!.id, previousData: record },
      {
        onSuccess: () => {
          notify("Invoice deleted successfully", { type: "success" });
          navigate("/invoices");
        },
        onError: () => {
          notify("Failed to delete invoice", { type: "error" });
        },
      },
    );
  };

  // Editable fields
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [status, setStatus] = useState("");

  // Initialize editing state when entering edit mode
  const handleStartEditing = () => {
    if (record) {
      setAmount(String(record.amount));
      setDueDate(record.due_date);
      setStatus(record.status);
      setIsEditing(true);
    }
  };

  if (isPending || !record) return null;

  const formatCurrency = (amt: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amt);

  const hasChanges =
    isEditing &&
    (Number(amount) !== record.amount ||
      dueDate !== record.due_date ||
      status !== record.status);

  const handleSave = () => {
    const updateData: Record<string, unknown> = {
      amount: Number(amount) || 0,
      due_date: dueDate,
      status,
    };

    // If changing to paid, set paid_at
    if (status === "paid" && record.status !== "paid") {
      updateData.paid_at = new Date().toISOString();
    }
    // If changing from paid to something else, clear paid_at
    if (status !== "paid" && record.status === "paid") {
      updateData.paid_at = null;
    }

    update(
      "invoices",
      {
        id: record.id,
        data: updateData,
        previousData: record,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          refresh();
        },
      },
    );
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleMarkAsPaid = () => {
    update(
      "invoices",
      {
        id: record.id,
        data: {
          status: "paid",
          paid_at: new Date().toISOString(),
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          refresh();
        },
      },
    );
  };

  const isPaidOrCanPay =
    record.status === "pending" || record.status === "overdue";

  if (isEditing) {
    return (
      <div className="p-4 max-w-2xl mx-auto">
        {/* Back button */}
        <Link
          to="/invoices"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Invoices
        </Link>

        {/* Main Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Edit Invoice #{record.id}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={isUpdating || !hasChanges}
                >
                  <Save className="h-4 w-4 mr-1" />
                  {isUpdating ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Amount */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Amount
              </Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-48"
              />
            </div>

            {/* Due Date */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Due Date
              </Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-48"
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Status
              </Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-48">
                  <SelectValue>
                    <Badge
                      className={statusColors[status] || statusColors.pending}
                    >
                      {statusChoices.find((c) => c.value === status)?.label ||
                        status}
                    </Badge>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {statusChoices.map((choice) => (
                    <SelectItem key={choice.value} value={choice.value}>
                      <Badge className={statusColors[choice.value]}>
                        {choice.label}
                      </Badge>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Read-only References */}
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Job:</span>
              <ReferenceField source="job_id" reference="jobs" link={false}>
                <JobLink jobId={record.job_id} />
              </ReferenceField>
            </div>

            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Proposal:</span>
              <Link
                to={`/proposals/${record.proposal_id}/show`}
                className="text-sm text-blue-600 hover:underline"
              >
                #{record.proposal_id}
              </Link>
            </div>

            {/* Paid At (if paid) */}
            {record.paid_at && (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-sm text-muted-foreground">Paid:</span>
                <span className="text-sm text-green-600 dark:text-green-400">
                  {new Date(record.paid_at).toLocaleDateString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto">
      {/* Back button and delete */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/invoices"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Invoices
        </Link>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm" disabled={isDeleting}>
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Invoice
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this invoice? This action cannot
                be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Main Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Invoice #{record.id}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge className={statusColors[record.status]}>
                {record.status}
              </Badge>
              <Button variant="outline" size="sm" onClick={handleStartEditing}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Amount */}
          <div className="text-center py-4">
            <span className="text-sm text-muted-foreground">Amount</span>
            <div className="text-4xl font-bold mt-1">
              {formatCurrency(record.amount)}
            </div>
          </div>

          <Separator />

          {/* Job Reference */}
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Job:</span>
            <ReferenceField source="job_id" reference="jobs" link={false}>
              <JobLink jobId={record.job_id} />
            </ReferenceField>
          </div>

          {/* Proposal Reference */}
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Proposal:</span>
            <Link
              to={`/proposals/${record.proposal_id}/show`}
              className="text-sm text-blue-600 hover:underline"
            >
              #{record.proposal_id}
            </Link>
          </div>

          <Separator />

          {/* Due Date */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Due Date:</span>
            <span className="text-sm">{record.due_date}</span>
          </div>

          {/* Paid At (if paid) */}
          {record.paid_at && (
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm text-muted-foreground">Paid:</span>
              <span className="text-sm text-green-600 dark:text-green-400">
                {new Date(record.paid_at).toLocaleDateString()}
              </span>
            </div>
          )}

          {/* Mark as Paid Button */}
          {isPaidOrCanPay && (
            <>
              <Separator />
              <div className="flex justify-center pt-2">
                <Button
                  onClick={handleMarkAsPaid}
                  disabled={isUpdating}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {isUpdating ? "Processing..." : "Mark as Paid"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Helper component for job link
const JobLink = ({ jobId }: { jobId: number }) => {
  const record = useRecordContext();
  if (!record) return <span>#{jobId}</span>;

  return (
    <Link
      to={`/jobs/${jobId}/show`}
      className="text-sm text-blue-600 hover:underline"
    >
      {record.address || `#${jobId}`}
    </Link>
  );
};
